import ExpoCachedImage from 'expo-cached-image';
import * as Device from 'expo-device';
import React, { useEffect, useState } from 'react';
import {
  Platform,
  Image,
  ImageResizeMode,
  StyleProp,
  ImageStyle,
} from 'react-native';

import { Box } from '@components/Restyle';
import { CDN_BASE_URL } from '@src/constants/constants';
import theme from '@themes/theme';
import { AsyncStorage } from '@utils/storage';

type CachedImageNoLoginPropsType = {
  imageClientId?: string;
  isProduction?: boolean;
  isThumbnail?: boolean;
  image: string;
  width?: number;
  screenWidth?: number;
  height?: number;
  marginRight?: number;
  isRemoveable?: boolean;
  croppingStrategy?: 'c-at_max_enlarge';
  resizeMode?: ImageResizeMode;
  borderRadius?: number;
  skipCache?: boolean;
  style?: Omit<StyleProp<ImageStyle>, 'width' | 'height' | 'opacity'>;
  placeholderContentFlex?: 0 | 1;
  from?: string;
};

const CachedImageNoLogin: React.FC<CachedImageNoLoginPropsType> = ({
  imageClientId,
  image,
  width,
  screenWidth,
  height,
  isRemoveable,
  borderRadius,
  resizeMode,
  croppingStrategy,
  skipCache = true,
  style: styleProp,
  placeholderContentFlex = 1,
  from = '',
  isProduction = false,
  isThumbnail = false,
}) => {
  const CachedImageForPlatform =
    Platform.OS === 'web' || skipCache ? Image : ExpoCachedImage;

  const cdnBaseUrl = CDN_BASE_URL;
  const isLocal = false;

  const format = 'f-auto';

  // ImageKit device density options:
  // https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations#dpr-dpr
  const density = Device.isDevice ? 'dpr-2' : 'dpr-1';

  // ImageKit image quality options:
  // https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations#quality-q
  // Note that a density setting of 2 is recommended to have a lower quality.

  let quality;

  if (from === 'search') {
    quality = Device.isDevice ? 'q-40' : 'q-60';
  } else {
    quality = Device.isDevice ? 'q-60' : 'q-80';
  }

  const transformations = [
    '/tr:',
    format,
    density,
    quality,
    width && `w-${width}`,
    height && `h-${height}`,
    croppingStrategy,
  ].filter(Boolean);

  const style: StyleProp<ImageStyle> = [
    {
      ...styleProp,
      width: screenWidth ?? width,
      height: height || '100%',
      opacity: isLocal && !isRemoveable ? 0.5 : 1.0,
      // A caller-provided backgroundColor (e.g. ImagesPreviewNotLogin.web.tsx
      // matching the dark MediaViewer stage, TA-10293) must win over this
      // default — previously it was clobbered because this key was spread
      // after ...styleProp regardless of whether styleProp set it.
      backgroundColor:
        (styleProp as { backgroundColor?: string } | undefined)
          ?.backgroundColor ??
        (Platform.OS === 'web' ? theme.colors.grey02 : 'white'),
      borderRadius:
        borderRadius ??
        (styleProp as { borderRadius?: number } | undefined)?.borderRadius,
    },
  ];

  const [imageUrl, setImageUrl] = useState('');
  useEffect(() => {
    const getUrl = async () => {
      if (imageClientId) {
        const cached = await AsyncStorage.getItem(imageClientId);
        if (cached) {
          setImageUrl(cached);
        } else {
          setTimeout(getUrl, 1000);
        }
      }
    };
    getUrl();
  }, [imageClientId]);

  const renderImage = () => {
    const transformationList = transformations.join(',');

    let cdnUrl = '';
    let cacheKey = '';

    if (imageClientId) {
      const cdnUrlBig =
        imageUrl ?? `${cdnBaseUrl}${transformationList}${image}`;
      cacheKey = imageClientId;
      cdnUrl = isThumbnail ? image : cdnUrlBig;
    } else {
      cdnUrl = `${
        (Platform.OS !== 'web' && isProduction) ||
        process.env.AASA_MODE === 'production' ||
        process.env.PROD_APIS === 'true'
          ? cdnBaseUrl + '/production'
          : cdnBaseUrl
      }${transformationList}/${image}`;

      cacheKey = `${transformationList}/${image}`.replace(/\W+/gi, '_');
    }

    return (
      <CachedImageForPlatform
        source={{
          uri: cdnUrl,
          expiresIn: undefined, // never expire
        }}
        cacheKey={cacheKey}
        style={style}
        resizeMode={resizeMode || 'cover'}
        placeholderContent={
          <Box
            backgroundColor='grey02'
            style={{
              flex: placeholderContentFlex,
              height,
              width,
              borderRadius: borderRadius || undefined,
            }}
          />
        }
      />
    );
  };

  return renderImage();
};

export default CachedImageNoLogin;
